import { tzParts } from '../utils/appClock'
// Desktop footer pieces (footer-20261031). Phone never renders these.
import { memo, useRef, useEffect } from 'react'

const fmtPrice = (p) => {
  if (!Number.isFinite(p) || p <= 0) return '-'
  if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (p >= 1) return p.toFixed(2)
  if (p >= 0.01) return p.toFixed(4)
  return p.toPrecision(3)
}

// Static one-line ticker. Same feed as the old scan ticker (tickerTokens);
// items that do not fit are clipped by overflow:hidden, never wrapped.
export const FooterTicker = memo(function FooterTicker({ tokens, onPick }) {
  const list = Array.isArray(tokens) ? tokens : []
  const boxRef = useRef(null)
  // Mouse wheel scrolls the ticker horizontally (fast). Non-passive so the page does not scroll.
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const onWheel = (e) => {
      const d = (Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX) * 3
      if (!d) return
      e.preventDefault()
      const half = el.scrollWidth / 2
      let x = el.scrollLeft + d
      if (half > 0) { if (x >= half) x -= half; if (x < 0) x += half }
      el.scrollLeft = x
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])
  // Continuous marquee: the list is rendered twice and the track slides by
  // exactly -50%, so the loop is seamless. Speed ~3s per item (old ticker).
  const item = (t, k) => {
    const addr = t.id || t.address || ''
    const price = Number(t.usdPrice ?? t.price ?? t._raw?.price)
    const chg = Number(t.stats24h?.priceChange ?? t.priceChange24h)
    const hasChg = Number.isFinite(chg)
    return (
      <span key={`${addr}-${k}`} className="df-tk" title={addr} onClick={() => addr && onPick?.(addr)}>
        <span className="df-sym">{t.symbol || t.name || addr.slice(0, 6)}</span>
        <span className="df-px">{fmtPrice(price)}</span>
        {hasChg && <span className={chg >= 0 ? 'df-up' : 'df-dn'}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</span>}
      </span>
    )
  }
  return (
    <div className="df-center" ref={boxRef}>
      {list.length > 0 && (
        <div className="df-track" style={{ '--df-dur': `${Math.max(20, list.length * 3)}s` }}>
          {list.map((t) => item(t, 'a'))}
          {list.map((t) => item(t, 'b'))}
        </div>
      )}
    </div>
  )
})

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtTs = (ms) => {
  const d = tzParts(ms)
  const p = (n) => String(n).padStart(2, '0')
  return `${MON[d.mon]} ${d.day} ${p(d.h)}:${p(d.m)}`
}
const fmtSpan = (ms) => {
  const h = ms / 3600000
  if (h >= 24) return `${(h / 24).toFixed(1)}d`
  if (h >= 1) return `${h.toFixed(1)}h`
  return `${Math.max(1, Math.round(ms / 60000))}m`
}

// Loaded range of the active chart. Amber while history/backfill is pending.
export const FooterDataRange = memo(function FooterDataRange({ chartData, pending }) {
  const c = chartData?.candles || []
  const n = c.length
  const first = n ? c[0]?.startTime : null
  const last = n ? c[n - 1]?.startTime : null
  const ok = Number.isFinite(first) && Number.isFinite(last)
  const tfMs = (Number(chartData?.tf) || 0) * 1000
  const end = ok ? last + tfMs : null
  return (
    <div className={`df-right${pending ? ' df-warn' : ''}`}
         title={ok ? `${n} bars, ${chartData?.tradeCount ?? 0} trades in memory${pending ? ' - history loading' : ''}` : 'No data loaded'}>
      <span className="df-tag">DATA</span>
      {ok ? (
        <span className="df-range">{fmtTs(first)} {'\u2192'} {fmtTs(end)} {'\u00b7'} {fmtSpan(end - first)} {'\u00b7'} {n.toLocaleString('en-US')} bars</span>
      ) : <span className="df-range">-</span>}
    </div>
  )
})
